import type { Edge, Node } from '@xyflow/react';
import type {
  AttackPathEventEdgeData,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';

const ENTITY_NODE_WIDTH = 240;
const ENTITY_NODE_HEIGHT = 72;
const SIGNAL_EXECUTION_PADDING = 28;

export const LIVE_FRONTIER_FRAME_ID = 'live-frontier';

const buildSignalExecutionNodeData = ({
  signalId,
  primaryExecutionId,
  objective,
  status,
  column,
  startedAt,
  isLive,
}: {
  signalId: string;
  primaryExecutionId: string | null;
  objective: string | null;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PENDING';
  column: number;
  startedAt: string | null;
  isLive: boolean;
}): AttackPathEventNodeData => ({
  role: 'SIGNAL_EXECUTION',
  campaignId: signalId,
  campaignStatus:
    !isLive && (status === 'RUNNING' || status === 'PENDING')
      ? 'ABORTED'
      : status === 'RUNNING' || status === 'PENDING'
        ? 'RUNNING'
        : status === 'SUCCEEDED'
          ? 'COMPLETED'
          : status === 'FAILED'
            ? 'ABORTED'
            : 'PLANNED',
  isCampaignRunning: isLive && (status === 'RUNNING' || status === 'PENDING'),
  chainProgress: status === 'SUCCEEDED' ? 1 : 0,
  kind: 'WEAKNESS_EXPLOITED',
  typeLabel: signalId,
  title: objective ?? signalId,
  subtitle: null,
  caption: null,
  weaknessId: null,
  mitreTechniqueId: null,
  provenance: null,
  privilegeTier: null,
  resourceKind: null,
  severity: null,
  status:
    status === 'FAILED'
      ? 'FAILED'
      : status === 'SUCCEEDED'
        ? 'SUCCEEDED'
        : 'IN_PROGRESS',
  isProven: status === 'SUCCEEDED',
  exploitsWeakness: false,
  isCrownJewel: false,
  isDomainController: false,
  impacts: [],
  elapsedSeconds: column,
  occurredAt: startedAt ?? new Date(0).toISOString(),
  hopIndex: null,
  retryCount: 0,
  findingId: null,
  hostname: null,
  entityKind: null,
  entityId: null,
  objective,
  signalId,
  primaryExecutionId,
  durationSeconds: null,
  chainStatus:
    status === 'FAILED' ||
    (!isLive && (status === 'RUNNING' || status === 'PENDING'))
      ? 'BLOCKED'
      : status === 'SUCCEEDED'
        ? 'COMPLETED'
        : 'IN_PROGRESS',
  column,
  lane: 0,
});

export const prependSignalExecutionFrame = ({
  nodes,
  framed,
  signalId,
  primaryExecutionId,
  objective,
  status,
  startedAt,
  isLive,
  selectable,
}: {
  nodes: Node<AttackPathEventNodeData>[];
  framed: Node<AttackPathEventNodeData>[];
  signalId: string;
  primaryExecutionId: string | null;
  objective: string | null;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PENDING';
  startedAt: string | null;
  isLive: boolean;
  selectable: boolean;
}): void => {
  if (framed.length === 0) {
    return;
  }

  const minX = Math.min(...framed.map((node) => node.position.x));
  const maxX = Math.max(...framed.map((node) => node.position.x));
  const minY = Math.min(...framed.map((node) => node.position.y));
  const maxY = Math.max(...framed.map((node) => node.position.y));
  const column = Math.min(...framed.map((node) => node.data.column));

  nodes.unshift({
    id: `signal-execution:${signalId}`,
    type: 'signalExecution',
    position: {
      x: minX - SIGNAL_EXECUTION_PADDING,
      y: minY - SIGNAL_EXECUTION_PADDING,
    },
    style: {
      width: maxX - minX + ENTITY_NODE_WIDTH + SIGNAL_EXECUTION_PADDING * 2,
      height: maxY - minY + ENTITY_NODE_HEIGHT + SIGNAL_EXECUTION_PADDING * 2,
      zIndex: -1,
    },
    selectable,
    data: buildSignalExecutionNodeData({
      signalId,
      primaryExecutionId,
      objective,
      status,
      column,
      startedAt,
      isLive,
    }),
  });
};

export const markNodeRunning = (
  node: Node<AttackPathEventNodeData>,
): Node<AttackPathEventNodeData> => {
  node.data = {
    ...node.data,
    isCampaignRunning: true,
    chainStatus: 'IN_PROGRESS',
    campaignStatus: 'RUNNING',
  };

  return node;
};

export const focusLatestRunningCampaign = ({
  nodes,
  edges,
}: {
  nodes: Node<AttackPathEventNodeData>[];
  edges: Edge<AttackPathEventEdgeData>[];
}): void => {
  const runningFrames = nodes.filter(
    (node) =>
      node.data.role === 'SIGNAL_EXECUTION' && node.data.isCampaignRunning,
  );

  if (runningFrames.length <= 1) {
    return;
  }

  const latestFrame = runningFrames.reduce((latest, frame) =>
    (frame.data.occurredAt ?? '') > (latest.data.occurredAt ?? '')
      ? frame
      : latest,
  );
  const latestCampaignId = latestFrame.data.campaignId;

  nodes.forEach((node) => {
    if (node.id === latestFrame.id) {
      return;
    }

    if (node.data.role === 'SIGNAL_EXECUTION' && node.data.isCampaignRunning) {
      node.data = {
        ...node.data,
        isCampaignRunning: false,
        campaignStatus: 'COMPLETED',
      };

      return;
    }

    if (
      node.data.isCampaignRunning &&
      node.data.campaignId !== latestCampaignId
    ) {
      node.data = {
        ...node.data,
        isCampaignRunning: false,
      };
    }
  });

  edges.forEach((edge) => {
    if (
      edge.data?.isCampaignRunning &&
      edge.data.campaignId !== latestCampaignId
    ) {
      edge.data = {
        ...edge.data,
        isCampaignRunning: false,
      };
    }
  });
};
